import axios from 'axios';
import { appConfig } from '@/config/app.config.js';
import { normalizeGtin } from '@/utils/barcode.js';
import { buildApiUrl } from '@/utils/apiBase.js';

/** Feste EAN für den manuellen API-Test-Button */
export const API_TEST_EAN = '5904555094116';

/**
 * URL des serverseitigen PHP-Proxys (berücksichtigt publicPath).
 */
export function getProductLookupUrl() {
  return buildApiUrl(appConfig.shopware.proxyPath || 'api/product-by-gtin.php');
}

/**
 * GET /api/variants … (nur für Doku / ggf. Tests)
 */
export function buildVariantSearchParams(gtin) {
  const g = normalizeGtin(gtin);
  return {
    limit: 1,
    'filter[0][property]': 'ean',
    'filter[0][value]': g,
  };
}

/**
 * Varianten-Antwort in ein einheitliches Ergebnis mappen (Artikel-ID, Varianten-ID, Artikelnummer).
 * @param {object} raw
 * @param {string} requestedGtin
 */
export function normalizeVariantRecord(raw, requestedGtin) {
  if (!raw || typeof raw !== 'object') {
    return {
      title: '',
      articleId: null,
      variantId: null,
      articleNumber: '',
      regelnummer: '',
      fachnummer: '',
      gtin: normalizeGtin(requestedGtin),
      raw,
    };
  }

  const article = raw.article || {};
  const attribute = raw.attribute || {};
  const articleNumber = String(
    raw.number ?? raw.articleNumber ?? '',
  ).trim();

  const title = String(
    article.name || raw.name || articleNumber || '',
  ).trim();

  const variantId = raw.id != null ? raw.id : null;
  const articleId =
    raw.articleId != null
      ? raw.articleId
      : article.id != null
        ? article.id
        : null;

  const gtin =
    raw.ean != null && String(raw.ean).trim() !== ''
      ? normalizeGtin(raw.ean)
      : normalizeGtin(requestedGtin);

  return {
    title,
    articleId,
    variantId,
    articleNumber,
    regelnummer: String(attribute.attr1 ?? '').trim(),
    fachnummer: String(attribute.attr2 ?? '').trim(),
    gtin,
    raw,
  };
}

/**
 * Artikel/Variante per GTIN — Aufruf über PHP-Proxy (keine Shopware-Zugangsdaten im Browser).
 *
 * @param {string} gtin Rohcode vom Scanner
 */
export async function searchProductByGtin(gtin) {
  const requested = normalizeGtin(gtin);
    if (!requested) {
    return {
      title: '',
      articleId: null,
      variantId: null,
      articleNumber: '',
      regelnummer: '',
      fachnummer: '',
      gtin: '',
      raw: null,
      found: false,
    };
  }

  const url = getProductLookupUrl();
  const timeout = appConfig.shopware.timeoutMs || 20000;

  try {
    const response = await axios.post(url, { gtin: requested }, {
      headers: { 'Content-Type': 'application/json' },
      timeout,
      withCredentials: true,
      validateStatus: () => true,
    });

    const { data, status } = response;
    if (status >= 400) {
      const e = new Error(
        (data && data.error) || `HTTP ${status}`,
      );
      e.status = status;
      e.body = data;
      throw e;
    }

    if (data && data.ok === false) {
      const e = new Error(data.error || 'API-Fehler');
      e.status = data.status;
      e.body = data;
      throw e;
    }

    if (!data || typeof data.found !== 'boolean') {
      const e = new Error('Ungültige Antwort vom Server');
      e.body = data;
      throw e;
    }

    if (!data.found) {
      return {
        title: '',
        articleId: null,
        variantId: null,
        articleNumber: '',
        regelnummer: '',
        fachnummer: '',
        imageUrl: '',
        gtin: requested,
        raw: data.raw ?? null,
        found: false,
      };
    }


    let title = String(data.title || '').trim();
    if (!title && data.articleNumber) {
      title = `Art.-Nr. ${data.articleNumber}`;
    }
    if (!title && data.articleId != null) {
      title = `Artikel #${data.articleId}`;
    }

    return {
      title,
      articleId: data.articleId ?? null,
      variantId: data.variantId ?? null,
      articleNumber: String(data.articleNumber || '').trim(),
      regelnummer: String(data.regelnummer ?? '').trim(),
      fachnummer: String(data.fachnummer ?? '').trim(),
      imageUrl: String(data.imageUrl || '').trim(),
      gtin: normalizeGtin(data.gtin || requested),
      raw: data.raw ?? null,
      found: true,
    };
  } catch (err) {
    if (err.response) {
      const status = err.response.status;
      const body = err.response.data;
      const e = new Error(
        body?.error || (status ? `HTTP ${status}` : err.message) || 'Netzwerkfehler',
      );
      e.cause = err;
      e.status = status;
      e.body = body;
      throw e;
    }
    throw err;
  }
}
